/**
 * Softwise M10b — memory-order hand flow engine (pure, type-only imports).
 *
 * Derives, from the recorded actions only:
 *   - who acts next (rule-derived), which actions are legal for a seat,
 *   - when a street closes (everyone matched + last player acted),
 *   - when the next board card(s) must be asked (street closed, 2+ players left),
 *   - when the hand is over (one player left, or river closed).
 *
 * Never inserts actions. Seats the user skips over (e.g. seats before the
 * first entrant) are "not involved": out of the hand and never shown in the
 * line (#61 — no fabricated folds). A skipped seat that posted a blind is
 * labeled "not recorded" and its blind stays in the pot. Folded / all-in seats never act again.
 *
 * Process-sharing only: no metrics, advice or evaluation here.
 */
import type { ActionType, HandState, Street, StreetAction } from './types'

export const FLOW_STREETS: Street[] = ['preflop', 'flop', 'turn', 'river']

/** Mirrors poker.getPositions (kept local so this module stays pure). */
export function flowPositions(playerCount: number): string[] {
  const count = Math.min(9, Math.max(2, playerCount))
  if (count === 2) return ['BTN', 'BB']
  const n = Math.max(0, count - 3)
  let mid: string[] = []
  if (n === 1) mid = ['UTG']
  else if (n === 2) mid = ['UTG', 'CO']
  else if (n === 3) mid = ['UTG', 'MP', 'CO']
  else if (n === 4) mid = ['UTG', 'MP', 'HJ', 'CO']
  else if (n === 5) mid = ['UTG', 'UTG+1', 'MP', 'HJ', 'CO']
  else if (n > 5) mid = ['UTG', 'UTG+1', 'MP', 'MP+1', 'HJ', 'CO'].slice(0, n)
  return [...mid, 'BTN', 'SB', 'BB']
}

/** Preflop acting order (first to act → BB). */
export function preflopOrder(playerCount: number): string[] {
  return flowPositions(playerCount)
}

/** Postflop acting order: SB, BB, then early → BTN. HU: BB then BTN. */
export function postflopOrder(playerCount: number): string[] {
  const pos = flowPositions(playerCount)
  if (pos.length === 2) return ['BB', 'BTN']
  const btn = pos.indexOf('BTN')
  return [...pos.slice(btn + 1), ...pos.slice(0, btn + 1)]
}

export function streetOrder(street: Street, playerCount: number): string[] {
  return street === 'preflop'
    ? preflopOrder(playerCount)
    : postflopOrder(playerCount)
}

/** Map a stored actor (`Hero`, seat code, custom text) to a seat code. */
export function actorSeat(actor: string, hand: Pick<HandState, 'heroPosition' | 'playerCount'>): string {
  const trimmed = actor.trim()
  if (trimmed.toLowerCase() === 'hero') return hand.heroPosition
  const pos = flowPositions(hand.playerCount)
  const hit = pos.find((p) => p.toLowerCase() === trimmed.toLowerCase())
  return hit ?? trimmed
}

/** Stored actor for a seat: hero seat stays `Hero` (legacy-compatible). */
export function seatActor(seat: string, hand: Pick<HandState, 'heroPosition'>): string {
  return seat === hand.heroPosition ? 'Hero' : seat
}

/** Parse `2.5bb` → 2.5, else null (legacy pot-fraction sizes → null). */
export function parseBb(raw: string | undefined): number | null {
  if (!raw) return null
  const m = raw.trim().match(/^(\d+(?:\.\d+)?)\s*bb$/i)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) ? n : null
}

/** Format a bb amount: 2.5 → `2.5bb`, 3 → `3bb`. */
export function formatBb(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return ''
  const rounded = Math.round(n * 100) / 100
  return `${String(rounded)}bb`
}

export function streetBoardComplete(hand: HandState, street: Street): boolean {
  if (street === 'preflop') return true
  if (street === 'flop') return hand.board.flop.every(Boolean)
  if (street === 'turn') return Boolean(hand.board.turn)
  return Boolean(hand.board.river)
}

export type FlowPhase =
  | { kind: 'actions'; street: Street; next: string }
  | { kind: 'board'; street: 'flop' | 'turn' | 'river' }
  | { kind: 'done'; reason: 'one-left' | 'river-closed' | 'memory-cut' }

export type SeatOptions = {
  /** Legal action types for this seat, in display order. */
  legal: ActionType[]
  /** Facing a bet/raise (amount to call > 0). */
  facing: boolean
  /** Current street bet level in bb (preflop starts at 1 = BB). */
  currentBet: number
  /** Size chips (bb) for bet/raise. */
  sizeChips: number[]
  /** Minimum custom size in bb (exclusive lower bound). */
  minSize: number
}

export type SkippedSeatStatus = 'unrecorded' | 'notInvolved'

export type HandFlow = {
  phase: FlowPhase
  /** Street whose actions are being entered (or last street reached). */
  street: Street
  /** Seats that may act now on this street (not folded/all-in/not involved, still to act). */
  pending: string[]
  /** Seats still in the hand (not folded, not "not involved"). */
  live: string[]
  folded: Set<string>
  allin: Set<string>
  notInvolved: Set<string>
  /** Pending seats that posted this street — may press 「기록 안 함」. */
  unrecordable: string[]
  /**
   * Label for each skipped (not-involved) seat: `unrecorded` when it already
   * put chips in (a posted blind — the chips stay in the pot), otherwise
   * `notInvolved` (posted nothing). Neither is a fold nor part of the line.
   */
  seatStatus: Record<string, SkippedSeatStatus>
  /** Chips in the pot so far in bb, blinds included (skipped blinds stay). */
  potBb: number
  /** Per-seat options for the current street (only for pending seats). */
  optionsFor: (seat: string) => SeatOptions | null
}

type StreetCtx = {
  street: Street
  order: string[]
  contrib: Record<string, number>
  currentBet: number
  pending: Set<string>
  cursor: number
  acted: number
}

function roundHalf(n: number): number {
  return Math.round(n * 2) / 2
}

/**
 * Derive the whole flow from recorded actions (pure).
 * Stack caps: hero uses heroStackBb; others use effectiveStackBb when set,
 * otherwise unknown (never capped).
 */
export function deriveHandFlow(hand: HandState): HandFlow {
  const pc = hand.playerCount
  const hero = hand.heroPosition
  const sbBb =
    Number.isFinite(hand.smallBlind) && Number.isFinite(hand.bigBlind) && hand.bigBlind > 0
      ? hand.smallBlind / hand.bigBlind
      : 0.5
  const folded = new Set<string>()
  const allin = new Set<string>()
  const notInvolved = new Set<string>()
  for (const s of hand.unrecordedSeats ?? []) notInvolved.add(s)
  const committed: Record<string, number> = {}
  const stackOf = (seat: string): number => {
    if (seat === hero && Number.isFinite(hand.heroStackBb) && hand.heroStackBb > 0) {
      return hand.heroStackBb
    }
    if (hand.effectiveStackBb != null && Number.isFinite(hand.effectiveStackBb) && hand.effectiveStackBb > 0) {
      return hand.effectiveStackBb
    }
    return Number.POSITIVE_INFINITY
  }
  const out = (s: string) => folded.has(s) || notInvolved.has(s)
  const canAct = (s: string) => !out(s) && !allin.has(s)
  const remaining = (s: string) => stackOf(s) - (committed[s] ?? 0)

  let ctx: StreetCtx | null = null
  let phase: FlowPhase | null = null

  const liveSeats = () => flowPositions(pc).filter((s) => !out(s))

  const seatOptions = (c: StreetCtx, seat: string): SeatOptions | null => {
    if (!c.pending.has(seat)) return null
    const toCall = Math.max(0, c.currentBet - (c.contrib[seat] ?? 0))
    const facing = toCall > 1e-9
    const rem = remaining(seat)
    const callIsAllin = facing && rem <= toCall + 1e-9
    const legal: ActionType[] = []
    const isPf = c.street === 'preflop'
    const openLevel = isPf ? c.currentBet <= 1 + 1e-9 : c.currentBet <= 1e-9
    const sizeChips = openLevel
      ? [2.5, 3]
      : [roundHalf(c.currentBet * 2.5), roundHalf(c.currentBet * 3)]
    if (facing) {
      legal.push('fold')
      if (!callIsAllin) legal.push('call')
      if (!callIsAllin) legal.push('raise')
      legal.push('allin')
    } else {
      legal.push('check')
      // Preflop with blinds in: the aggressive option is a raise (BB option).
      legal.push(isPf ? 'raise' : 'bet')
      legal.push('allin')
    }
    return {
      legal,
      facing,
      currentBet: c.currentBet,
      sizeChips: sizeChips.filter((n) => n < rem + 1e-9 && n > c.currentBet),
      minSize: c.currentBet,
    }
  }

  for (let si = 0; si < FLOW_STREETS.length; si++) {
    const street = FLOW_STREETS[si]!
    const streetActions = hand.actions.filter((a) => a.street === street)
    if (street !== 'preflop') {
      if (liveSeats().length <= 1) {
        phase = { kind: 'done', reason: 'one-left' }
        break
      }
      if (!streetBoardComplete(hand, street) && streetActions.length === 0) {
        phase = { kind: 'board', street: street as 'flop' | 'turn' | 'river' }
        break
      }
    }
    const order = streetOrder(street, pc)
    const c: StreetCtx = {
      street,
      order,
      contrib: {},
      currentBet: 0,
      pending: new Set(order.filter(canAct)),
      cursor: 0,
      acted: 0,
    }
    if (street === 'preflop') {
      const sbSeat = pc === 2 ? 'BTN' : 'SB'
      c.contrib[sbSeat] = Math.min(sbBb, stackOf(sbSeat))
      c.contrib.BB = Math.min(1, stackOf('BB'))
      committed[sbSeat] = c.contrib[sbSeat]!
      committed.BB = c.contrib.BB!
      c.currentBet = 1
    }
    ctx = c
    // Street with ≤1 seat able to act and nothing recorded: nothing to ask.
    if (streetActions.length === 0 && order.filter(canAct).length <= 1 && street !== 'preflop') {
      continue
    }
    for (const a of streetActions) applyAction(c, a)
    if (liveSeats().length <= 1) {
      phase = { kind: 'done', reason: 'one-left' }
      break
    }
    if (c.pending.size > 0) {
      phase = { kind: 'actions', street, next: nextPending(c) }
      break
    }
  }
  if (!phase) phase = { kind: 'done', reason: 'river-closed' }

  // Soft Quiet C2: voluntary mid-cut — stop asking further board / actions.
  // Recorded actions stay; never fabricate folds / sizes / board.
  if (hand.memoryCutAt != null && phase.kind !== 'done') {
    phase = { kind: 'done', reason: 'memory-cut' }
  }

  const cur = ctx
  const pendingList =
    phase.kind === 'actions' && cur
      ? rotate(cur.order, cur.cursor).filter((s) => cur.pending.has(s))
      : []
  const seatStatus: Record<string, SkippedSeatStatus> = {}
  for (const s of flowPositions(pc)) {
    if (!notInvolved.has(s)) continue
    seatStatus[s] = (committed[s] ?? 0) > 1e-9 ? 'unrecorded' : 'notInvolved'
  }
  const potBb = Object.values(committed).reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0)
  const unrecordable =
    phase.kind === 'actions'
      ? pendingList.filter((s) => (committed[s] ?? 0) > 1e-9)
      : []
  return {
    phase,
    street:
      phase.kind === 'actions'
        ? phase.street
        : phase.kind === 'board'
          ? phase.street
          : cur?.street ?? 'preflop',
    pending: pendingList,
    live: liveSeats(),
    folded,
    allin,
    notInvolved,
    unrecordable,
    seatStatus,
    potBb: Math.round(potBb * 100) / 100,
    optionsFor: (seat: string) =>
      phase!.kind === 'actions' && cur ? seatOptions(cur, seat) : null,
  }

  function rotate(order: string[], start: number): string[] {
    const n = order.length
    const s = ((start % n) + n) % n
    return [...order.slice(s), ...order.slice(0, s)]
  }

  /**
   * Where the "next actor" suggestion starts. Preflop before anyone entered:
   * the user's own seat (memory order starts with hero). Skipping still
   * counts from the first seat, so earlier seats stay selectable.
   */
  function suggestStart(c: StreetCtx): number {
    if (c.street === 'preflop' && c.acted === 0) {
      const heroIdx = c.order.indexOf(hero)
      if (heroIdx >= 0 && c.pending.has(hero)) return heroIdx
    }
    return c.cursor
  }

  function nextPending(c: StreetCtx): string {
    for (const s of rotate(c.order, suggestStart(c))) if (c.pending.has(s)) return s
    return c.order[0]!
  }

  function applyAction(c: StreetCtx, a: StreetAction): void {
    const seat = actorSeat(a.actor, hand)
    const idx = c.order.indexOf(seat)
    if (idx < 0 || !canAct(seat)) return
    // Softwise M10f: seats with nothing posted may be skipped as 「참여 안 함」.
    // Blind posters (committed > 0) stay pending until the user presses
    // 「기록 안 함」 — never auto-marked (Soft-safe ⑤).
    for (const s of rotate(c.order, c.cursor)) {
      if (s === seat) break
      if (!c.pending.has(s)) continue
      if ((committed[s] ?? 0) > 1e-9) return // cannot jump over a blind poster
      c.pending.delete(s)
      notInvolved.add(s)
    }
    const before = c.contrib[seat] ?? 0
    const put = (target: number) => {
      const add = Math.max(0, target - before)
      const capped = Math.min(add, remaining(seat))
      c.contrib[seat] = before + capped
      committed[seat] = (committed[seat] ?? 0) + capped
      if (remaining(seat) <= 1e-9) allin.add(seat)
    }
    const reopen = () => {
      c.pending = new Set(c.order.filter((s) => s !== seat && canAct(s)))
    }
    switch (a.type) {
      case 'fold':
        folded.add(seat)
        c.pending.delete(seat)
        break
      case 'check':
        c.pending.delete(seat)
        break
      case 'call':
        put(c.currentBet)
        c.pending.delete(seat)
        break
      case 'bet':
      case 'raise': {
        const size = parseBb(a.size)
        // Legacy pot-fraction sizes: amount unknown — still a raise.
        const target = size != null && size > c.currentBet ? size : c.currentBet + 1
        put(target)
        c.currentBet = Math.max(c.currentBet, c.contrib[seat] ?? target)
        reopen()
        break
      }
      case 'allin': {
        const rem = remaining(seat)
        const size = parseBb(a.size)
        const target = Number.isFinite(rem)
          ? before + rem
          : size != null
            ? Math.max(size, before)
            : c.currentBet + 1
        put(target)
        allin.add(seat)
        const now = c.contrib[seat] ?? target
        if (now > c.currentBet + 1e-9) {
          c.currentBet = now
          reopen()
        } else {
          c.pending.delete(seat)
        }
        break
      }
    }
    c.acted++
    c.cursor = idx + 1
  }
}

/**
 * Softwise M10d: the hand is over because only one player is still in
 * (everyone else folded — folds the user entered, never fabricated). The
 * input jumps straight to the share card: no next board card, no showdown,
 * no opponent cards. River-closed hands keep the normal flow.
 */
export function handEndsOnePlayerLeft(flow: Pick<HandFlow, 'phase'>): boolean {
  return flow.phase.kind === 'done' && flow.phase.reason === 'one-left'
}

/**
 * Soft Quiet C2: user pressed 「기억 안 남」 — hand ends as honest incomplete.
 * No further board / action / villain asks; line stops at recorded actions.
 */
export function handEndsMemoryCut(flow: Pick<HandFlow, 'phase'>): boolean {
  return flow.phase.kind === 'done' && flow.phase.reason === 'memory-cut'
}
